import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, finalize, map, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { StateCleanupService } from './state-cleanup.service';

// get date in yyyy-mm-dd format minus days
function getDateMinusDays(days: number) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split('T')[0];
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  _dateRange: number;
  _interval: 'day' | 'week' | 'month';
  private _tutorId: string | null = null;
  private _moodleCourseId: number | null = null;
  private _moodleCourseUuid: string | null = null;
  private _moodleRegistrationId: string | null = null;

  dateFrom: string;
  dateTo: string;

  isLoading$ = new BehaviorSubject<string[]>([]);
  _isDemo$ = new BehaviorSubject<string[]>([]);

  multiLineSessionsAndAvgDurations$ = new BehaviorSubject<any>(undefined);
  barSessions$ = new BehaviorSubject<any>(undefined);
  barTotalDuration$ = new BehaviorSubject<any>(undefined);
  barAvgDuration$ = new BehaviorSubject<any>(undefined);
  sessionsKpisSummary$ = new BehaviorSubject<any>(undefined);
  multiLineEventTypes$ = new BehaviorSubject<any>(undefined);
  donutEventSplit$ = new BehaviorSubject<any>(undefined);
  hbarEngagementRatePerEntity$ = new BehaviorSubject<any>(undefined);
  hbarEventCountsPerEntity$ = new BehaviorSubject<any>(undefined);
  hstackThumbsPerEntity$ = new BehaviorSubject<any>(undefined);
  sessionsHeatmap$ = new BehaviorSubject<any>(undefined);
  eventsKpisSummary$ = new BehaviorSubject<any>(undefined);
  lineEventEngagementRate$ = new BehaviorSubject<any>(undefined);
  lineAvgEventsPerSession$ = new BehaviorSubject<any>(undefined);
  lineEventExercisePerformance$ = new BehaviorSubject<any>(undefined);
  eventsHeatmap$ = new BehaviorSubject<any>(undefined);

  semanticsTopics$ = new BehaviorSubject<any>(undefined);
  semanticsTopicsTree$ = new BehaviorSubject<any>(undefined);
  semanticsDistributionsBloomIntentTopic$ = new BehaviorSubject<any>(undefined);
  semanticsTimeSeriesBloom$ = new BehaviorSubject<any>(undefined);
  semanticsTimeSeriesTopic$ = new BehaviorSubject<any>(undefined);
  semanticsTimeSeriesIntent$ = new BehaviorSubject<any>(undefined);

  semanticsDonutBloom$ = new BehaviorSubject<any>(undefined);
  semanticsDonutTopic$ = new BehaviorSubject<any>(undefined);
  semanticsDonutIntent$ = new BehaviorSubject<any>(undefined);

  semanticsRankingTopicsCanonicals$ = new BehaviorSubject<any>(undefined);
  // semanticsDonutTopicsSplit$ = new BehaviorSubject<any>(undefined);

  realTimeSessionsPerMinute$ = new BehaviorSubject<any>(undefined);
  realTimeLast30mTotals$ = new BehaviorSubject<any>(undefined);
  realTimeEventsFeed$ = new BehaviorSubject<any>(undefined);
  realTimeGaugeCurrentVsPeak$ = new BehaviorSubject<any>(undefined);

  // Effective analytics retention (days) for this client — plan-dependent,
  // served by /analytics/retention. null until loaded.
  retentionDays$ = new BehaviorSubject<number | null>(null);
  retentionOverrideUntil: string | null = null;

  constructor(
    protected http: HttpClient,
    private stateCleanupService: StateCleanupService
  ) {
    this._dateRange = localStorage.getItem('al-analytics-date-range')
      ? parseInt(localStorage.getItem('al-analytics-date-range')!, 10)
      : 7;

    this._interval =
      (localStorage.getItem('al-analytics-interval') as any) || 'day';

    this.dateFrom = getDateMinusDays(this._dateRange - 1);
    // this.dateTo = getDateMinusDays(0);
    this.stateCleanupService.register(() => this.resetState());

    const storedTutorId = localStorage.getItem('al-analytics-tutor-id');
    this._tutorId = storedTutorId ? storedTutorId : null;

    const storedMoodleCourseId = localStorage.getItem(
      'al-analytics-moodle-course-id'
    );
    const parsedMoodleCourseId = storedMoodleCourseId
      ? Number(storedMoodleCourseId)
      : null;
    this._moodleCourseId =
      parsedMoodleCourseId !== null && Number.isFinite(parsedMoodleCourseId)
        ? parsedMoodleCourseId
        : null;
    this._moodleCourseUuid =
      localStorage.getItem('al-analytics-moodle-course-uuid') || null;
    this._moodleRegistrationId =
      localStorage.getItem('al-analytics-moodle-registration-id') || null;

    if (
      this._tutorId &&
      (this._moodleCourseId !== null ||
        this._moodleCourseUuid ||
        this._moodleRegistrationId)
    ) {
      this.clearMoodleFilterStorage();
    }
  }

  // https://lassedesk.top/al/tutors/analytics/retention
  fetchRetention() {
    if (this.retentionDays$.value !== null) {
      return;
    }

    this.http
      .get(`${environment.tutorsBackend}/analytics/retention`)
      .subscribe((d: any) => {
        this.retentionOverrideUntil = d.override_until || null;
        this.retentionDays$.next(d.retention_days);
      });
  }

  fetchSessionsData() {
    this.getMultiLineSessionsAndAvgDurations().subscribe();
    this.getBarSessions().subscribe();
    this.getBarTotalDuration().subscribe();
    this.getBarAvgDuration().subscribe();
    this.getKpisSummary().subscribe();
    this.getSessionsHeatmap().subscribe();
  }

  fetchEventsData() {
    this.getMultiLineEventTypes().subscribe();
    this.getDonutEventSplit().subscribe();
    this.getHbarEngagementRatePerEntity().subscribe();
    this.getHbarEventCountsPerEntity().subscribe();
    this.getHstackThumbsPerEntity().subscribe();
    this.getEventsKpisSummary().subscribe();
    this.getLineEventEngagementRate().subscribe();
    this.getLineAvgEventsPerSession().subscribe();
    this.getLineEventExercisePerformance().subscribe();
    this.getEventsHeatmap().subscribe();
  }

  fetchSemanticsData() {
    this.getSemanticsTopics().subscribe();
    this.getSemanticsTopicsTree().subscribe();
    this.getSemanticsDonutBloom().subscribe();
    this.getSemanticsDonutTopic().subscribe();
    this.getSemanticsDonutIntent().subscribe();
    this.getSemanticsTimeSeriesBloom().subscribe();
    this.getSemanticsTimeSeriesTopic().subscribe();
    this.getSemanticsTimeSeriesIntent().subscribe();
    this.getSemanticsRankingTopicsCanonicals().subscribe();
    // this.getSemanticsDonutTopicsSplit().subscribe();
  }

  fetchLiveData() {
    this.getRealTimeSessionsPerMinute().subscribe();
    this.getRealTimeLast30mTotals().subscribe();
    this.getRealTimeEventsFeed().subscribe();
    this.getRealTimeGaugeCurrentVsPeak().subscribe();
  }

  get body() {
    const body: {
      date_from: string;
      date_to: string;
      interval: 'day' | 'week' | 'month';
      bot_id?: string;
      moodle_course_id?: number;
      moodle_course_uuid?: string;
      moodle_registration_id?: string;
    } = {
      date_from: this.dateFrom,
      date_to: this.dateTo,
      interval: this.interval,
    };

    if (this.tutorId) {
      body.bot_id = this.tutorId;
    }

    if (this.moodleCourseId !== null) {
      body.moodle_course_id = this.moodleCourseId;
    }
    if (this.moodleCourseUuid) {
      body.moodle_course_uuid = this.moodleCourseUuid;
    }
    if (this.moodleRegistrationId) {
      body.moodle_registration_id = this.moodleRegistrationId;
    }

    return body;
  }

  private get realtimeBody(): {
    bot_id?: string;
    moodle_course_id?: number;
    moodle_course_uuid?: string;
    moodle_registration_id?: string;
  } {
    const body: {
      bot_id?: string;
      moodle_course_id?: number;
      moodle_course_uuid?: string;
      moodle_registration_id?: string;
    } = {};
    if (this.tutorId) {
      body.bot_id = this.tutorId;
    }
    if (this.moodleCourseId !== null) {
      body.moodle_course_id = this.moodleCourseId;
    }
    if (this.moodleCourseUuid) {
      body.moodle_course_uuid = this.moodleCourseUuid;
    }
    if (this.moodleRegistrationId) {
      body.moodle_registration_id = this.moodleRegistrationId;
    }
    return body;
  }

  setLoading(type: string, loading: boolean): void {
    if (this.isLoading$.value.includes(type) && loading) {
      return;
    }

    this.isLoading$.next(
      loading
        ? [...this.isLoading$.value, type]
        : [...this.isLoading$.value.filter((t) => t !== type)]
    );
  }

  setDemo(type: string, isDemo: boolean): void {
    if (this._isDemo$.value.includes(type) && isDemo) {
      return;
    }

    this._isDemo$.next(
      isDemo
        ? [...this._isDemo$.value, type]
        : [...this._isDemo$.value.filter((t) => t !== type)]
    );
  }

  isDemo(type: string): boolean {
    return this._isDemo$.value.includes(type);
  }

  /**
   *
   * ! SESSIONS
   *
   */
  // https://lassedesk.top/al/tutors/analytics/sessions/multi-line-sessions-and-avg-durations
  getMultiLineSessionsAndAvgDurations() {
    this.setLoading('multiLineSessionsAndAvgDurations', true);
    return this.http
      .post(
        `${environment.tutorsBackend}/analytics/sessions/multi-line-sessions-and-avg-durations`,
        {
          ...this.body,
        }
      )
      .pipe(
        finalize(() => this.setLoading('multiLineSessionsAndAvgDurations', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('multiLineSessionsAndAvgDurations', data.is_demo);
          this.multiLineSessionsAndAvgDurations$.next(data);
          this.setLoading('multiLineSessionsAndAvgDurations', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/sessions/hbar-sessions
  getBarSessions() {
    this.setLoading('barSessions', true);
    return this.http
      .post(`${environment.tutorsBackend}/analytics/sessions/hbar-sessions`, {
        ...this.body,
      })
      .pipe(
        finalize(() => this.setLoading('barSessions', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('barSessions', data.is_demo);
          this.barSessions$.next(data);
          this.setLoading('barSessions', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/sessions/hbar-total-duration
  getBarTotalDuration() {
    this.setLoading('barTotalDuration', true);
    return this.http
      .post(
        `${environment.tutorsBackend}/analytics/sessions/hbar-total-duration`,
        {
          ...this.body,
        }
      )
      .pipe(
        finalize(() => this.setLoading('barTotalDuration', false)),
        map((d: any) => d.data),
        tap((data: any) => {
          this.setDemo('barTotalDuration', data.is_demo);
          this.barTotalDuration$.next(data);
          this.setLoading('barTotalDuration', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/sessions/hbar-avg-duration
  getBarAvgDuration() {
    this.setLoading('barAvgDuration', true);
    return this.http
      .post(
        `${environment.tutorsBackend}/analytics/sessions/hbar-avg-duration`,
        {
          ...this.body,
        }
      )
      .pipe(
        finalize(() => this.setLoading('barAvgDuration', false)),
        map((d: any) => d.data),
        tap((data: any) => {
          this.setDemo('barAvgDuration', data.is_demo);
          this.barAvgDuration$.next(data);
          this.setLoading('barAvgDuration', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/sessions/kpis-summary
  getKpisSummary() {
    this.setLoading('sessionsKpisSummary', true);
    return this.http
      .post(`${environment.tutorsBackend}/analytics/sessions/kpis-summary`, {
        ...this.body,
      })
      .pipe(
        finalize(() => this.setLoading('sessionsKpisSummary', false)),
        tap((data: any) => {
          this.setDemo('sessionsKpisSummary', data.is_demo);
          this.sessionsKpisSummary$.next(data);
          this.setLoading('sessionsKpisSummary', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/sessions/heatmap
  getSessionsHeatmap() {
    this.setLoading('sessionsHeatmap', true);
    return this.http
      .post(`${environment.tutorsBackend}/analytics/sessions/heatmap`, {
        ...this.body,
      })
      .pipe(
        finalize(() => this.setLoading('sessionsHeatmap', false)),
        tap((data: any) => {
          this.setDemo('sessionsHeatmap', data.is_demo);
          this.sessionsHeatmap$.next(data);
          this.setLoading('sessionsHeatmap', false);
        })
      );
  }

  /**
   *
   * ! Events
   *
   */
  // https://lassedesk.top/al/tutors/analytics/events/multi-line-event-types
  getMultiLineEventTypes() {
    this.setLoading('multiLineEventTypes', true);
    return this.http
      .post(
        `${environment.tutorsBackend}/analytics/events/multi-line-event-types`,
        {
          ...this.body,
        }
      )
      .pipe(
        finalize(() => this.setLoading('multiLineEventTypes', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('multiLineEventTypes', data.is_demo);
          this.multiLineEventTypes$.next(data);
          this.setLoading('multiLineEventTypes', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/events/donut-event-split
  getDonutEventSplit() {
    this.setLoading('donutEventSplit', true);
    return this.http
      .post(`${environment.tutorsBackend}/analytics/events/donut-event-split`, {
        ...this.body,
      })
      .pipe(
        finalize(() => this.setLoading('donutEventSplit', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('donutEventSplit', data.is_demo);
          this.donutEventSplit$.next(data);
          this.setLoading('donutEventSplit', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/events/hbar-engagement-rate-per-entity
  getHbarEngagementRatePerEntity() {
    this.setLoading('hbarEngagementRatePerEntity', true);
    return this.http
      .post(
        `${environment.tutorsBackend}/analytics/events/hbar-engagement-rate-per-entity`,
        {
          ...this.body,
        }
      )
      .pipe(
        finalize(() => this.setLoading('hbarEngagementRatePerEntity', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('hbarEngagementRatePerEntity', data.is_demo);
          this.hbarEngagementRatePerEntity$.next(data);
          this.setLoading('hbarEngagementRatePerEntity', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/events/hbar-event-counts-per-entity
  getHbarEventCountsPerEntity() {
    this.setLoading('hbarEventCountsPerEntity', true);
    return this.http
      .post(
        `${environment.tutorsBackend}/analytics/events/hbar-event-counts-per-entity`,
        {
          ...this.body,
        }
      )
      .pipe(
        finalize(() => this.setLoading('hbarEventCountsPerEntity', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('hbarEventCountsPerEntity', data.is_demo);
          this.hbarEventCountsPerEntity$.next(data);
          this.setLoading('hbarEventCountsPerEntity', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/events/hstack-thumbs-per-entity
  getHstackThumbsPerEntity() {
    this.setLoading('hstackThumbsPerEntity', true);
    return this.http
      .post(
        `${environment.tutorsBackend}/analytics/events/hstack-thumbs-per-entity`,
        {
          ...this.body,
        }
      )
      .pipe(
        finalize(() => this.setLoading('hstackThumbsPerEntity', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('hstackThumbsPerEntity', data.is_demo);
          this.hstackThumbsPerEntity$.next(data);
          this.setLoading('hstackThumbsPerEntity', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/events/kpis-summary
  getEventsKpisSummary() {
    this.setLoading('eventsKpisSummary', true);
    return this.http
      .post(`${environment.tutorsBackend}/analytics/events/kpis-summary`, {
        ...this.body,
      })
      .pipe(
        finalize(() => this.setLoading('eventsKpisSummary', false)),
        tap((data) => {
          this.eventsKpisSummary$.next(data);
          this.setLoading('eventsKpisSummary', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/events/line-engagement-rate
  getLineEventEngagementRate() {
    this.setLoading('lineEventEngagementRate', true);
    return this.http
      .post(
        `${environment.tutorsBackend}/analytics/events/line-engagement-rate`,
        {
          ...this.body,
        }
      )
      .pipe(
        finalize(() => this.setLoading('lineEventEngagementRate', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('lineEventEngagementRate', data.is_demo);
          this.lineEventEngagementRate$.next(data);
          this.setLoading('lineEventEngagementRate', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/events/line-avg-events-per-session
  getLineAvgEventsPerSession() {
    this.setLoading('lineAvgEventsPerSession', true);
    return this.http
      .post(
        `${environment.tutorsBackend}/analytics/events/line-avg-events-per-session`,
        {
          ...this.body,
        }
      )
      .pipe(
        finalize(() => this.setLoading('lineAvgEventsPerSession', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('lineAvgEventsPerSession', data.is_demo);
          this.lineAvgEventsPerSession$.next(data);
          this.setLoading('lineAvgEventsPerSession', false);
        })
      );
  }
  // https://lassedesk.top/al/tutors/analytics/events/line-exercise-performance
  getLineEventExercisePerformance() {
    this.setLoading('lineEventExercisePerformance', true);
    return this.http
      .post(
        `${environment.tutorsBackend}/analytics/events/line-exercise-performance`,
        {
          ...this.body,
        }
      )
      .pipe(
        finalize(() => this.setLoading('lineEventExercisePerformance', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('lineEventExercisePerformance', data.is_demo);
          this.lineEventExercisePerformance$.next(data);
          this.setLoading('lineEventExercisePerformance', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/events/heatmap
  getEventsHeatmap() {
    this.setLoading('eventsHeatmap', true);
    return this.http
      .post(`${environment.tutorsBackend}/analytics/events/heatmap`, {
        ...this.body,
      })
      .pipe(
        finalize(() => this.setLoading('eventsHeatmap', false)),
        tap((data: any) => {
          this.setDemo('eventsHeatmap', data.is_demo);
          this.eventsHeatmap$.next(data);
          this.setLoading('eventsHeatmap', false);
        })
      );
  }

  /**
   *
   * ! Realtime
   *
   */

  // https://lassedesk.top/al/tutors/analytics/realtime/sessions-per-minute
  getRealTimeSessionsPerMinute() {
    this.setLoading('realTimeSessionsPerMinute', true);
    return this.http
      .post(
        `${environment.tutorsBackend}/analytics/realtime/sessions-per-minute`,
        { ...this.realtimeBody }
      )
      .pipe(
        finalize(() => this.setLoading('realTimeSessionsPerMinute', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('realTimeSessionsPerMinute', data.is_demo);
          this.realTimeSessionsPerMinute$.next(data);
          this.setLoading('realTimeSessionsPerMinute', false);
        })
      );
  }
  // https://lassedesk.top/al/tutors/analytics/realtime/last-30m-totals
  getRealTimeLast30mTotals() {
    this.setLoading('realTimeLast30mTotals', true);
    return this.http
      .post(`${environment.tutorsBackend}/analytics/realtime/last-30m-totals`, {
        ...this.realtimeBody,
      })
      .pipe(
        finalize(() => this.setLoading('realTimeLast30mTotals', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('realTimeLast30mTotals', data.is_demo);
          this.realTimeLast30mTotals$.next(data);
          this.setLoading('realTimeLast30mTotals', false);
        })
      );
  }
  // https://lassedesk.top/al/tutors/analytics/realtime/events/feed
  getRealTimeEventsFeed() {
    this.setLoading('realTimeEventsFeed', true);
    return this.http
      .post(`${environment.tutorsBackend}/analytics/realtime/events/feed`, {
        ...this.realtimeBody,
      })
      .pipe(
        finalize(() => this.setLoading('realTimeEventsFeed', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('realTimeEventsFeed', data.is_demo);
          this.realTimeEventsFeed$.next(data);
          this.setLoading('realTimeEventsFeed', false);
        })
      );
  }
  // https://lassedesk.top/al/tutors/analytics/realtime/gauge-current-vs-peak
  getRealTimeGaugeCurrentVsPeak() {
    this.setLoading('realTimeGaugeCurrentVsPeak', true);
    return this.http
      .post(
        `${environment.tutorsBackend}/analytics/realtime/gauge-current-vs-peak`,
        { ...this.realtimeBody }
      )
      .pipe(
        finalize(() => this.setLoading('realTimeGaugeCurrentVsPeak', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.realTimeGaugeCurrentVsPeak$.next(data);
          this.setLoading('realTimeGaugeCurrentVsPeak', false);
        })
      );
  }

  /**
   *
   * ! Semantics
   *
   */

  // https://lassedesk.top/al/tutors/analytics/semantics/topics
  // ! missing in UI
  getSemanticsTopics() {
    this.setLoading('semanticsTopics', true);
    return this.http
      .post(`${environment.tutorsBackend}/analytics/semantics/topics`, {
        ...this.body,
      })
      .pipe(
        finalize(() => this.setLoading('semanticsTopics', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('semanticsTopics', data.is_demo);
          this.semanticsTopics$.next(data);
          this.setLoading('semanticsTopics', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/semantics/topic-tree
  getSemanticsTopicsTree() {
    this.setLoading('semanticsTopicsTree', true);
    return this.http
      .post(`${environment.tutorsBackend}/analytics/semantics/topic-tree`, {
        ...this.body,
        interval: undefined,
      })
      .pipe(
        finalize(() => this.setLoading('semanticsTopicsTree', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('semanticsTopicsTree', data.is_demo);
          this.semanticsTopicsTree$.next(data);
          this.setLoading('semanticsTopicsTree', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/semantics/donut/bloom
  getSemanticsDonutBloom() {
    this.setLoading('semanticsDonutBloom', true);
    return this.http
      .post(`${environment.tutorsBackend}/analytics/semantics/donut/bloom`, {
        ...this.body,
      })
      .pipe(
        finalize(() => this.setLoading('semanticsDonutBloom', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('semanticsDonutBloom', data.is_demo);
          this.semanticsDonutBloom$.next(data);
          this.setLoading('semanticsDonutBloom', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/semantics/donut/intent
  getSemanticsDonutIntent() {
    this.setLoading('semanticsDonutIntent', true);
    return this.http
      .post(`${environment.tutorsBackend}/analytics/semantics/donut/intent`, {
        ...this.body,
      })
      .pipe(
        finalize(() => this.setLoading('semanticsDonutIntent', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('semanticsDonutIntent', data.is_demo);
          this.semanticsDonutIntent$.next(data);
          this.setLoading('semanticsDonutIntent', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/semantics/donut/topic
  getSemanticsDonutTopic() {
    this.setLoading('semanticsDonutTopic', true);
    return this.http
      .post(`${environment.tutorsBackend}/analytics/semantics/donut/topic`, {
        ...this.body,
      })
      .pipe(
        finalize(() => this.setLoading('semanticsDonutTopic', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('semanticsDonutTopic', data.is_demo);
          this.semanticsDonutTopic$.next(data);
          this.setLoading('semanticsDonutTopic', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/semantics/time-series-bloom
  getSemanticsTimeSeriesBloom() {
    this.setLoading('semanticsTimeSeriesBloom', true);
    return this.http
      .post(
        `${environment.tutorsBackend}/analytics/semantics/time-series-bloom`,
        {
          ...this.body,
        }
      )
      .pipe(
        finalize(() => this.setLoading('semanticsTimeSeriesBloom', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('semanticsTimeSeriesBloom', data.is_demo);
          this.semanticsTimeSeriesBloom$.next(data);
          this.setLoading('semanticsTimeSeriesBloom', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/semantics/time-series-topic
  getSemanticsTimeSeriesTopic() {
    this.setLoading('semanticsTimeSeriesTopic', true);
    return this.http
      .post(
        `${environment.tutorsBackend}/analytics/semantics/time-series-topic`,
        {
          ...this.body,
        }
      )
      .pipe(
        finalize(() => this.setLoading('semanticsTimeSeriesTopic', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('semanticsTimeSeriesTopic', data.is_demo);
          this.semanticsTimeSeriesTopic$.next(data);
          this.setLoading('semanticsTimeSeriesTopic', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/semantics/time-series-intent
  getSemanticsTimeSeriesIntent() {
    this.setLoading('semanticsTimeSeriesIntent', true);
    return this.http
      .post(
        `${environment.tutorsBackend}/analytics/semantics/time-series-intent`,
        {
          ...this.body,
        }
      )
      .pipe(
        finalize(() => this.setLoading('semanticsTimeSeriesIntent', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('semanticsTimeSeriesIntent', data.is_demo);
          this.semanticsTimeSeriesIntent$.next(data);
          this.setLoading('semanticsTimeSeriesIntent', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/semantics/ranking-topics-canonicals
  getSemanticsRankingTopicsCanonicals() {
    this.setLoading('semanticsRankingTopicsCanonicals', true);
    return this.http
      .post(
        `${environment.tutorsBackend}/analytics/semantics/ranking-topics-canonicals`,
        {
          ...this.body,
        }
      )
      .pipe(
        finalize(() => this.setLoading('semanticsRankingTopicsCanonicals', false)),
        map((d: any) => d.data),
        tap((data) => {
          this.setDemo('semanticsRankingTopicsCanonicals', data.is_demo);
          this.semanticsRankingTopicsCanonicals$.next(data);
          this.setLoading('semanticsRankingTopicsCanonicals', false);
        })
      );
  }

  // https://lassedesk.top/al/tutors/analytics/semantics/donut-topics-split
  // getSemanticsDonutTopicsSplit() {
  //   this.setLoading('semanticsDonutTopicsSplit', true);
  //   return this.http
  //     .post(
  //       `${environment.tutorsBackend}/analytics/semantics/donut-topics-split`,
  //       {
  //         ...this.body,
  //       }
  //     )
  //     .pipe(
  //       map((d: any) => d.data),
  //       tap((data) => {
  //         this.setDemo('semanticsDonutTopicsSplit', data.is_demo);
  //         this.semanticsDonutTopicsSplit$.next(data);
  //         this.setLoading('semanticsDonutTopicsSplit', false);
  //       })
  //     );
  // }

  get dateRange() {
    return this._dateRange;
  }

  set dateRange(value: number) {
    localStorage.setItem('al-analytics-date-range', value.toString());
    this._dateRange = value;
  }

  get interval() {
    return this._interval;
  }

  set interval(value: 'day' | 'week' | 'month') {
    localStorage.setItem('al-analytics-interval', value);
    this._interval = value;
  }

  get tutorId(): string | null {
    return this._tutorId;
  }

  set tutorId(value: string | null) {
    this._tutorId = value;

    if (value) {
      localStorage.setItem('al-analytics-tutor-id', value);
      this.clearMoodleFilterStorage();
    } else {
      localStorage.removeItem('al-analytics-tutor-id');
    }
  }

  get moodleCourseId(): number | null {
    return this._moodleCourseId;
  }

  set moodleCourseId(value: number | string | null) {
    const parsedValue = value === null || value === '' ? null : Number(value);
    this._moodleCourseId =
      parsedValue !== null && Number.isFinite(parsedValue) ? parsedValue : null;

    if (this._moodleCourseId !== null) {
      localStorage.setItem(
        'al-analytics-moodle-course-id',
        this._moodleCourseId.toString()
      );
      this._tutorId = null;
      localStorage.removeItem('al-analytics-tutor-id');
    } else {
      localStorage.removeItem('al-analytics-moodle-course-id');
    }
  }

  get moodleCourseUuid(): string | null {
    return this._moodleCourseUuid;
  }

  set moodleCourseUuid(value: string | null) {
    this._moodleCourseUuid = value || null;

    if (this._moodleCourseUuid) {
      localStorage.setItem(
        'al-analytics-moodle-course-uuid',
        this._moodleCourseUuid
      );
      this._tutorId = null;
      this._moodleCourseId = null;
      localStorage.removeItem('al-analytics-tutor-id');
      localStorage.removeItem('al-analytics-moodle-course-id');
    } else {
      localStorage.removeItem('al-analytics-moodle-course-uuid');
    }
  }

  get moodleRegistrationId(): string | null {
    return this._moodleRegistrationId;
  }

  set moodleRegistrationId(value: string | null) {
    this._moodleRegistrationId = value || null;

    if (this._moodleRegistrationId) {
      localStorage.setItem(
        'al-analytics-moodle-registration-id',
        this._moodleRegistrationId
      );
      this._tutorId = null;
      localStorage.removeItem('al-analytics-tutor-id');
    } else {
      localStorage.removeItem('al-analytics-moodle-registration-id');
    }
  }

  private clearMoodleFilterStorage() {
    this._moodleCourseId = null;
    this._moodleCourseUuid = null;
    this._moodleRegistrationId = null;
    localStorage.removeItem('al-analytics-moodle-course-id');
    localStorage.removeItem('al-analytics-moodle-course-uuid');
    localStorage.removeItem('al-analytics-moodle-registration-id');
  }

  private resetState() {
    this.isLoading$.next([]);
    this._isDemo$.next([]);
    this.retentionDays$.next(null);
    this.retentionOverrideUntil = null;
    this.multiLineSessionsAndAvgDurations$.next(undefined);
    this.barSessions$.next(undefined);
    this.barTotalDuration$.next(undefined);
    this.barAvgDuration$.next(undefined);
    this.sessionsKpisSummary$.next(undefined);
    this.multiLineEventTypes$.next(undefined);
    this.donutEventSplit$.next(undefined);
    this.hbarEngagementRatePerEntity$.next(undefined);
    this.hbarEventCountsPerEntity$.next(undefined);
    this.hstackThumbsPerEntity$.next(undefined);
    this.sessionsHeatmap$.next(undefined);
    this.eventsKpisSummary$.next(undefined);
    this.lineEventEngagementRate$.next(undefined);
    this.lineAvgEventsPerSession$.next(undefined);
    this.lineEventExercisePerformance$.next(undefined);
    this.eventsHeatmap$.next(undefined);
    this.semanticsTopics$.next(undefined);
    this.semanticsTopicsTree$.next(undefined);
    this.semanticsDistributionsBloomIntentTopic$.next(undefined);
    this.semanticsTimeSeriesBloom$.next(undefined);
    this.semanticsTimeSeriesTopic$.next(undefined);
    this.semanticsTimeSeriesIntent$.next(undefined);
    this.semanticsDonutBloom$.next(undefined);
    this.semanticsDonutTopic$.next(undefined);
    this.semanticsDonutIntent$.next(undefined);
    this.semanticsRankingTopicsCanonicals$.next(undefined);
    this.realTimeSessionsPerMinute$.next(undefined);
    this.realTimeLast30mTotals$.next(undefined);
    this.realTimeEventsFeed$.next(undefined);
    this.realTimeGaugeCurrentVsPeak$.next(undefined);
  }
}
